// src/utils/venueApi.ts -- API functions for authenticated venue endpoints

import type { Venue, RedemptionCode } from '../types/venue';
import { getAuthHeader } from './api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5089';

async function apiFetch(path: string, options: RequestInit = {}) {
  const url = `${API_BASE_URL}${path}`;

  const res = await fetch(url, {
    ...options,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      // D-002: attach the Clerk bearer token via the shared choke point. All
      // /api/venues/* routes are owner-guarded (requireOwner) under
      // AUTH_ENFORCE=on; anonymous callers resolve to no header.
      ...(await getAuthHeader()),
      ...(options.headers || {}),
    },
  });

  if (!res.ok) {
    let errorBody = '';
    try {
      errorBody = await res.text();
    } catch (e) {
      errorBody = 'Unable to read error response';
    }
    throw new Error(`HTTP ${res.status} ${res.statusText}: ${errorBody}`);
  }

  if (res.status === 204) return null;

  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) return null;

  const text = await res.text();
  const result = text ? JSON.parse(text) : null;
  return result;
}

// Separate fetch helper for blob responses (PDF downloads)
async function apiFetchBlob(path: string, options: RequestInit = {}): Promise<Blob> {
  const url = `${API_BASE_URL}${path}`;

  const res = await fetch(url, {
    ...options,
    headers: {
      // D-002: bearer token only — no Content-Type so the blob/download
      // response handling is untouched. Omitted when anonymous.
      ...(await getAuthHeader()),
      ...(options.headers || {}),
    },
  });

  if (!res.ok) {
    let errorBody = '';
    try {
      errorBody = await res.text();
    } catch (e) {
      errorBody = 'Unable to read error response';
    }
    throw new Error(`HTTP ${res.status} ${res.statusText}: ${errorBody}`);
  }

  return res.blob();
}

/* ---------------- Venue CRUD ---------------- */

// Create venue (onboarding)
export function createVenue(venueData: Partial<Venue>): Promise<Venue> {
  return apiFetch('/api/venues', {
    method: 'POST',
    body: JSON.stringify(venueData),
  });
}

// Get venue for current user
export function fetchVenueByUser(userId: string): Promise<Venue> {
  return apiFetch(`/api/venues?userId=${encodeURIComponent(userId)}`);
}

// Soft probe — returns true if a venue exists for the user, false on 404,
// throws on other errors. Used by the persona hook so we don't pollute the
// console with expected 404s for users who don't run a venue.
export async function userHasVenue(userId: string): Promise<boolean> {
  const url = `${API_BASE_URL}/api/venues?userId=${encodeURIComponent(userId)}`;
  try {
    // D-002: GET /api/venues is owner-guarded — carry the bearer token so the
    // probe doesn't get a spurious 403 for a signed-in venue owner.
    const res = await fetch(url, {
      headers: { Accept: 'application/json', ...(await getAuthHeader()) },
    });
    if (res.ok) return true;
    if (res.status === 404) return false;
    return false;
  } catch {
    return false;
  }
}

// Get venue by ID
export function fetchVenueById(id: string): Promise<Venue> {
  return apiFetch(`/api/venues/${id}`);
}

// Update venue settings
export function updateVenue(id: string, venueData: Partial<Venue>): Promise<Venue> {
  return apiFetch(`/api/venues/${id}`, {
    method: 'PUT',
    body: JSON.stringify(venueData),
  });
}

// Delete venue
export function deleteVenue(id: string): Promise<void> {
  return apiFetch(`/api/venues/${id}`, {
    method: 'DELETE',
  });
}

/* ---------------- QR / PDF ---------------- */

// Download print-ready QR PDF (returns Blob, not JSON) -- AC-6
export function fetchQrPdf(venueId: string, size: string = 'table_tent'): Promise<Blob> {
  return apiFetchBlob(`/api/venues/${venueId}/qr-pdf?size=${encodeURIComponent(size)}`);
}

/* ---------------- Redemptions ---------------- */

// List all redemption codes for a venue
export function fetchRedemptions(venueId: string): Promise<RedemptionCode[]> {
  return apiFetch(`/api/venues/${venueId}/redemptions`);
}

// Look up a specific redemption code
export function lookupRedemption(venueId: string, code: string): Promise<RedemptionCode> {
  return apiFetch(`/api/venues/${venueId}/redemptions/${encodeURIComponent(code)}`);
}

// Mark a redemption code as redeemed
export function redeemCode(venueId: string, code: string): Promise<RedemptionCode> {
  return apiFetch(`/api/venues/${venueId}/redemptions/${encodeURIComponent(code)}/redeem`, {
    method: 'PUT',
  });
}

/* ---------------- Analytics ---------------- */

// Aggregate venue analytics (visits, unique guests, survey averages)
export function fetchVenueAnalytics(venueId: string): Promise<any> {
  return apiFetch(`/api/venues/${venueId}/analytics`);
}

// Survey response breakdown
export function fetchSurveyAnalytics(venueId: string): Promise<any> {
  return apiFetch(`/api/venues/${venueId}/analytics/surveys`);
}

// Visit timeline
export function fetchVisitTimeline(venueId: string, period: string = '7d'): Promise<any> {
  return apiFetch(`/api/venues/${venueId}/analytics/visits?period=${encodeURIComponent(period)}`);
}

/* ---------------- Menu Management ---------------- */

// Update menu config
export function updateMenu(venueId: string, menuData: any): Promise<Venue> {
  return apiFetch(`/api/venues/${venueId}/menu`, {
    method: 'PUT',
    body: JSON.stringify(menuData),
  });
}

// Upload PDF menu (multipart/form-data)
export async function uploadMenuPdf(venueId: string, file: File): Promise<any> {
  const formData = new FormData();
  formData.append('menu', file);

  const url = `${API_BASE_URL}/api/venues/${venueId}/menu/upload`;

  // D-002: attach the bearer token WITHOUT a Content-Type so the browser sets
  // the multipart boundary itself. Only add a `headers` object when a token is
  // actually available, so anonymous uploads stay header-less (browser boundary
  // handling unchanged).
  const authHeader = await getAuthHeader();
  const res = await fetch(url, {
    method: 'POST',
    body: formData,
    ...(Object.keys(authHeader).length ? { headers: authHeader } : {}),
  });

  if (!res.ok) {
    let errorBody = '';
    try {
      errorBody = await res.text();
    } catch (e) {
      errorBody = 'Unable to read error response';
    }
    throw new Error(`HTTP ${res.status} ${res.statusText}: ${errorBody}`);
  }
  return res.json();
}

/* ---------------- NFC Interest ---------------- */

// Record NFC interest (sends email notification to admin)
export function recordNfcInterest(venueId: string): Promise<any> {
  return apiFetch(`/api/venues/${venueId}/nfc-interest`, {
    method: 'POST',
  });
}
